/**
 * emit-opencode.ts — PortableSession -> OpenCode export JSON.
 *
 * Ported from the validated prototype (`emit/opencode.mjs`). The returned object
 * is exactly the shape `opencode import <file>` reads; the caller (fork.ts) writes it to a
 * temp file and runs `opencode import`, which re-homes the session to the CWD's
 * project.
 *
 * Key mappings:
 *   user text block                 -> user message with one TextPart
 *   assistant thinking              -> ReasoningPart (NO signature; already dropped)
 *   assistant text                  -> TextPart
 *   tool_call + its tool_result     -> ONE completed ToolPart (status "error" when is_error)
 *   a returned tool_result closes the assistant turn, so the next assistant
 *   block starts a new message.
 * Part ids sort lexically in transcript order; messages sort by time.created.
 */

import type { PortableSession } from "./extractor.js";
import { renderResumeContext } from "./resume-context.js";

// The OpenCode export/import envelope. Deep part/message shapes are OpenCode-
// internal, so they carry `any` bodies rather than re-declaring OpenCode's schema.
export interface OpenCodeExport {
  info: Record<string, any>;
  messages: Array<{ info: Record<string, any>; parts: any[] }>;
}

const tsMs = (ts: string | null | undefined): number | null => {
  if (!ts) return null;
  const n = Date.parse(ts);
  return Number.isNaN(n) ? null : n;
};

// --- tool NAME + ARG normalization (CC -> OpenCode native conventions) ---
// The resumed model imitates the arg names it sees in the imported history, so CC's
// snake_case (file_path/old_string) makes it call OpenCode's `read` with `file_path`
// -> "invalid arguments". We rewrite each mapped tool's name + input keys to OpenCode's.
//
// VERIFIED against anomalyco/opencode@v1.17.13 src/tool/*.ts (effect Schema.Struct params):
//   read {filePath,offset,limit} · edit {filePath,oldString,newString,replaceAll}
//   write {content,filePath} · bash {command,timeout,workdir} · grep {pattern,path,include}
//   glob {pattern,path} · webfetch {url,format,timeout} · websearch {query,...}
//   todowrite {todos} · task {description,prompt,subagent_type,...}
const NAME_MAP: Record<string, string> = {
  bash: "bash", read: "read", edit: "edit", write: "write", grep: "grep",
  glob: "glob", webfetch: "webfetch", websearch: "websearch",
  todowrite: "todowrite", task: "task",
};
const OC_ARGS: Record<string, { rename: Record<string, string>; keep: string[] }> = {
  bash:      { rename: {},                        keep: ["command", "timeout", "workdir"] },
  read:      { rename: { file_path: "filePath" }, keep: ["filePath", "offset", "limit"] },
  edit:      { rename: { file_path: "filePath", old_string: "oldString", new_string: "newString", replace_all: "replaceAll" },
              keep: ["filePath", "oldString", "newString", "replaceAll"] },
  write:     { rename: { file_path: "filePath" }, keep: ["content", "filePath"] },
  grep:      { rename: { glob: "include" },       keep: ["pattern", "path", "include"] },
  glob:      { rename: {},                        keep: ["pattern", "path"] },
  webfetch:  { rename: {},                        keep: ["url", "format", "timeout"] },
  websearch: { rename: {},                        keep: ["query", "numResults", "livecrawl", "type", "contextMaxCharacters"] },
  todowrite: { rename: {},                        keep: ["todos"] },
  task:      { rename: {},                        keep: ["description", "prompt", "subagent_type", "task_id", "command"] },
};

// CC tools with no OpenCode built-in equivalent (LS, MultiEdit, NotebookEdit, MCP tools,
// …) pass through unchanged — we do NOT invent a native mapping (would teach bad args).
function normalizeToolCall(ccName: string | undefined, input: any): { tool: string; input: any } {
  const ocId = NAME_MAP[String(ccName || "").toLowerCase()];
  if (!ocId) return { tool: ccName || "tool", input: input ?? {} };
  const spec = OC_ARGS[ocId];
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(input || {})) {
    const nk = spec.rename[k] || k;
    if (spec.keep.includes(nk)) out[nk] = v;
  }
  return { tool: ocId, input: out };
}

function toolTitle(name: string, input: any): string {
  const inp = input || {};
  if (typeof inp.command === "string") return inp.command.slice(0, 100);
  if (inp.filePath) return `${name} ${inp.filePath}`;
  if (inp.path) return `${name} ${inp.path}`;
  if (inp.pattern) return `${name} ${inp.pattern}`;
  if (inp.url) return `${name} ${inp.url}`;
  return name;
}

// Render a tool_result into the model-facing output string. Prefer the readable
// `content` (that's what CC shows the model); fall back to structured stdout/stderr.
function renderOutput(r: any): string {
  if (r.content != null) return typeof r.content === "string" ? r.content : JSON.stringify(r.content);
  const s = r.structured;
  if (s != null) {
    if (typeof s === "string") return s;
    if (typeof s.stdout === "string" || typeof s.stderr === "string") {
      return [s.stdout, s.stderr].filter((x: any) => x).join("\n");
    }
    return JSON.stringify(s);
  }
  return "";
}

// opencode part-metadata is NOT model-facing (renderOutput already builds the text
// the model sees), so we only carry the exit code.
function buildMeta(r: any): Record<string, any> {
  const s = r.structured;
  if (s && typeof s === "object" && typeof s.exitCode === "number") return { exit: s.exitCode };
  return {};
}

/**
 * Convert a PortableSession into the OpenCode export/import JSON. Pure: no I/O.
 */
export function renderOpenCode(session: PortableSession, orientation?: string, nonce?: string): OpenCodeExport {
  const meta = session.meta || ({} as PortableSession["meta"]);
  const cwd = meta.cwd || ".";

  // The session's model must be one that resume can actually call on this machine,
  // else `opencode run -s <id>` (no -m) can't get a reply. Default to an authed
  // OpenAI model; override with OPENCODE_RESUME_MODEL=provider/model.
  const RESUME_MODEL = process.env.OPENCODE_RESUME_MODEL || "openai/gpt-5.4";
  const slash = RESUME_MODEL.indexOf("/");
  const providerID = slash > 0 ? RESUME_MODEL.slice(0, slash) : "opencode";
  const modelID = slash > 0 ? RESUME_MODEL.slice(slash + 1) : RESUME_MODEL;

  // --- id helpers: prefix-correct, 26-char base62 body, lexically sortable ---
  // `nonce` makes every import mint fresh ids. opencode's `import` keys sessions
  // AND their messages/parts by id and no-ops on a collision, so a deterministic
  // id silently resurrects a stale prior import (the re-import never lands, and
  // you resume old content). The seed is constant within one render, so per-
  // message/-part ordering (by the index suffix) is preserved — it only differs
  // across imports. Without a nonce the ids stay deterministic (pure/testable).
  const pad26 = (s: string): string =>
    (String(s).replace(/[^A-Za-z0-9]/g, "") + "0".repeat(26)).slice(0, 26);
  const seed = (nonce || meta.source_session_id || "session").replace(/[^A-Za-z0-9]/g, "").slice(0, 10);
  const ses = "ses_" + pad26("sf" + seed);
  const msgId = (i: number): string => "msg_" + pad26(seed + "m" + String(i).padStart(6, "0"));
  const partId = (mi: number, pi: number): string =>
    "prt_" + pad26(seed + "m" + String(mi).padStart(6, "0") + "p" + String(pi).padStart(4, "0"));

  // Monotonic clock so message time.created strictly ascends regardless of ts gaps.
  let clock = tsMs(meta.started_at) || 1782980000000;
  const nextTime = (ts: string | null | undefined): number => {
    const t = tsMs(ts);
    clock = t != null && t > clock ? t : clock + 1000;
    return clock;
  };

  // --- group the flat timeline into messages ---
  const ordered = (Array.isArray(session.timeline) ? session.timeline : [])
    .map((b, i) => ({ b, i }))
    .sort((x, y) => (x.b.seq ?? x.i) - (y.b.seq ?? y.i) || x.i - y.i)
    .map((x) => x.b);

  interface Msg { info: Record<string, any>; parts: any[]; _mi: number; _pi: number; _created: number }
  const messages: Msg[] = [];
  const toolParts: Record<string, any> = {}; // CC tool_use id -> ToolPart (fold results in even out of order)
  let cur: Msg = null as unknown as Msg;
  let turnClosed = false;

  function newMessage(role: "user" | "assistant", ts: string | null | undefined): Msg {
    const created = nextTime(ts);
    const id = msgId(messages.length + 1);
    const prevId = messages.length ? messages[messages.length - 1].info.id : undefined;
    const info =
      role === "user"
        ? {
            id, sessionID: ses, role: "user",
            time: { created },
            ...(prevId ? { parentID: prevId } : {}),
            agent: "build",
            model: { providerID, modelID },
          }
        : {
            id, sessionID: ses, role: "assistant",
            time: { created },
            ...(prevId ? { parentID: prevId } : {}),
            modelID, providerID,
            mode: "build", agent: "build",
            path: { cwd, root: cwd },
            cost: 0,
            tokens: { input: 0, output: 0, reasoning: 0, cache: { read: 0, write: 0 } },
          };
    cur = { info, parts: [], _mi: messages.length + 1, _pi: 0, _created: created };
    messages.push(cur);
    return cur;
  }
  function addPart(p: any): any {
    cur._pi += 1;
    p.id = partId(cur._mi, cur._pi);
    p.sessionID = ses;
    p.messageID = cur.info.id;
    cur.parts.push(p);
    return p;
  }

  for (const b of ordered) {
    if (b.role === "user") {
      if (!cur || cur.info.role !== "user") newMessage("user", b.ts);
      if (b.text != null && b.text !== "") addPart({ type: "text", text: b.text });
      turnClosed = false;
    } else if (b.role === "assistant") {
      if (!cur || cur.info.role !== "assistant" || turnClosed) {
        newMessage("assistant", b.ts);
        turnClosed = false;
      }
      if (b.kind === "thinking") {
        const start = tsMs(b.ts) ?? cur._created;
        addPart({ type: "reasoning", text: b.text || "", time: { start, end: start + 500 } });
      } else if (b.kind === "text") {
        addPart({ type: "text", text: b.text || "" });
      } else if (b.kind === "tool_call") {
        const tool = b.tool || ({} as NonNullable<typeof b.tool>);
        const start = tsMs(b.ts) ?? cur._created;
        const { tool: ocTool, input: ocInput } = normalizeToolCall(tool.name, tool.input);
        const part = addPart({
          type: "tool",
          callID: "call_" + (tool.id || "t" + cur._pi),
          tool: ocTool,
          state: {
            status: "running", // finalized when the tool_result folds in (below)
            input: ocInput,
            title: toolTitle(ocTool, ocInput),
            time: { start, end: start + 1 },
          },
        });
        if (tool.id) toolParts[tool.id] = part;
      }
    } else if (b.role === "tool") {
      // fold this tool_result into the ToolPart its tool_use created
      const r = b.result || {};
      const part = r.tool_use_id != null ? toolParts[r.tool_use_id] : undefined;
      const out = renderOutput(r);
      const end = tsMs(b.ts);
      if (part) {
        part.state.status = r.is_error ? "error" : "completed";
        part.state.output = out;
        if (r.is_error) part.state.error = out || "tool error";
        part.state.metadata = buildMeta(r);
        if (end != null) part.state.time.end = end;
      } else {
        // orphan result (no matching call captured) -> degrade to a text note
        if (!cur || cur.info.role !== "assistant") newMessage("assistant", b.ts);
        addPart({ type: "text", text: "[tool result]\n" + out });
      }
      turnClosed = true;
    }
  }

  // Any ToolPart that never got a result must not stay "running".
  for (const m of messages) {
    for (const p of m.parts) {
      if (p.type === "tool" && p.state.status === "running") {
        p.state.status = "completed";
        if (p.state.output == null) p.state.output = "(no result captured)";
      }
    }
  }

  // Carry the where-am-I / what's-next state as a clearly-labeled USER-side context
  // note (never a fabricated assistant turn). It omits per-command outcomes by
  // design — those live in the tool parts above.
  const ctx = renderResumeContext(session);
  if (ctx) {
    newMessage("user", null);
    addPart({ type: "text", text: ctx });
  }

  // The fork's worktree/branch interview, injected as the agent's OWN closing
  // turn — a completed assistant message. buildOrientation is already written in
  // the agent's voice and carries the "won't switch branches/stash without your
  // say-so" guard, so it needs no wrapper: opencode renders it as the model
  // having greeted the user and then waits for their choice (not a QUEUED,
  // role-confused user note). Same convention as the claude-code/goose trailing
  // assistant turn. The `completed` stamp marks the turn done so resume doesn't
  // treat it as mid-generation.
  if (orientation) {
    const greeting = newMessage("assistant", null);
    greeting.info.time = { created: greeting._created, completed: greeting._created + 1 };
    addPart({ type: "text", text: orientation });
  }

  const info = {
    id: ses,
    slug: (meta.task_goal || meta.source_session_id || "resumed-session")
      .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 50) || "resumed-session",
    projectID: "global", // `opencode import` re-homes to the CWD's project anyway
    directory: cwd,
    path: cwd.replace(/^\/+/, ""),
    title: (meta.task_goal || "Resumed session").slice(0, 120),
    agent: "build",
    model: { id: modelID, providerID, variant: "default" },
    version: "1.17.13",
    cost: 0,
    tokens: { input: 0, output: 0, reasoning: 0, cache: { read: 0, write: 0 } },
    time: { created: messages[0] ? messages[0].info.time.created : clock, updated: clock },
  };

  return { info, messages: messages.map((m) => ({ info: m.info, parts: m.parts })) };
}
